// src/lib/api.ts
import type { ApiError } from './types'

class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

async function handleResponse<T>(res: Response, url: string): Promise<T> {
  if (res.status === 401 && !url.includes('/api/auth/login')) {
    window.location.href = '/login'
    throw new ApiClientError('unauthorized', 'Authentification requise', 401)
  }
  if (!res.ok) {
    let payload: ApiError
    try {
      payload = (await res.json()) as ApiError
    } catch {
      throw new ApiClientError('network_error', res.statusText, res.status)
    }
    throw new ApiClientError(
      payload.error.code,
      payload.error.message,
      res.status,
    )
  }
  return res.json() as Promise<T>
}

export const api = {
  get<T>(path: string): Promise<T> {
    return fetch(path, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).then((res) => handleResponse<T>(res, path))
  },

  post<T>(path: string, body: unknown): Promise<T> {
    return fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((res) => handleResponse<T>(res, path))
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return fetch(path, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((res) => handleResponse<T>(res, path))
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return fetch(path, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((res) => handleResponse<T>(res, path))
  },

  del<T>(path: string): Promise<T> {
    return fetch(path, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).then((res) => handleResponse<T>(res, path))
  },
}

export { ApiClientError }
